import { NOYAU_CODES, type NoyauCode, noyauCodeSchema } from '@rosetta/shared';
import { z } from 'zod';

/**
 * Search parameters of the result routes. The URL is a trust boundary like any
 * other: a value we do not know is dropped (or reset to its default) instead of
 * breaking the page.
 */

/**
 * `/login?redirection=&motif=` (B5, #13): the page to come back to once signed in (checked by
 * `redirectionSure` before use), and why the user is here (`session-expiree`: the login page says
 * the session expired).
 */
export const rechercheLoginSchema = z.object({
  redirection: z.string().optional().catch(undefined),
  motif: z.enum(['session-expiree']).optional().catch(undefined),
});
export type RechercheLogin = z.infer<typeof rechercheLoginSchema>;

/**
 * `/historique?recherche=&noyaux=` (B8, #13): the search and the noyau filter live in the address,
 * so that "retour" from a card finds them again. `noyaux` is a comma-separated list of codes; an
 * unknown code is dropped.
 */
export const rechercheHistoriqueSchema = z.object({
  recherche: z.string().optional().catch(undefined),
  noyaux: z.string().optional().catch(undefined),
});
export type RechercheHistorique = z.infer<typeof rechercheHistoriqueSchema>;

/** The noyaux of `?noyaux=SGP,GSP`, in charter order; an unknown code is dropped. */
export function noyauxDeLAdresse(brut: string | undefined): NoyauCode[] {
  const codes = (brut ?? '').split(',');
  return NOYAU_CODES.filter((code) => codes.includes(code));
}

/**
 * `?noyau=origine` (#38): the first tab of the result, the text as it was entered, which is no
 * noyau. One parameter says which tab is shown, so a link to a noyau and a link to the original
 * text are written the same way.
 */
export const ONGLET_ORIGINE = 'origine';

/** A tab of the result: the original text, or the translation of one noyau. */
export type ChoixResultat = NoyauCode | typeof ONGLET_ORIGINE;

/**
 * `/resultat/$id?noyau=`: the displayed tab, the first translated noyau when absent. A value that
 * is neither a noyau code nor `origine` is dropped: the page opens on the default tab.
 */
export const rechercheResultatSchema = z.object({
  noyau: z
    .union([noyauCodeSchema, z.literal(ONGLET_ORIGINE)])
    .optional()
    .catch(undefined),
});
export type RechercheResultat = z.infer<typeof rechercheResultatSchema>;

/** The three tabs of the analysis (#10: the two "à déterminer" ones became the last two). */
export const ONGLETS_ANALYSE = ['avant-apres', 'texte-origine', 'texte-traduit'] as const;
export type OngletAnalyse = (typeof ONGLETS_ANALYSE)[number];

/**
 * `/resultat/$id/analyse?noyau=&onglet=`: same noyau, and the tab ("Avant / après" by default,
 * and for a tab it does not know, the old `a-determiner-1` and `a-determiner-2` included).
 */
export const rechercheAnalyseSchema = z.object({
  noyau: noyauCodeSchema.optional().catch(undefined),
  onglet: z.enum(ONGLETS_ANALYSE).catch('avant-apres'),
});
export type RechercheAnalyse = z.infer<typeof rechercheAnalyseSchema>;
